import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, View } from 'react-native';
import { Link } from 'expo-router';

import ClosingBand from '@/components/Landing/ClosingBand';
import FlowField from '@/components/Landing/FlowField';
import {
  DisplayHeading,
  Kicker,
  Lede,
  Section,
  useInteractive,
  useLanding,
} from '@/components/Landing/LandingKit';
import SemanticBlock from '@/components/Landing/SemanticBlock';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import { EASE, LAYOUT, fluid, isWeb } from '@/components/Landing/landingTokens';
import { MicroLabel } from '@/components/Landing/MarketingKit';
import Reveal from '@/components/Landing/Reveal';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import USE_CASE_HUB_CATEGORIES from '@/constants/useCaseHubCategories';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '@/hooks/useUseCaseMenu';
import { useCasesApi } from '@/lib/api/useCases';

function FilterPill({ label, isActive, onPress }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Pressable
      {...handlers}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      style={{
        paddingHorizontal: 16,
        paddingVertical: 9,
        borderRadius: 9999,
        borderWidth: 1,
        borderColor: isActive
          ? (hovered ? c.accent : c.accentRing)
          : (hovered ? c.ruleStrong : c.rule),
        backgroundColor: isActive
          ? (hovered ? c.accentSoft : c.accentWash)
          : (hovered ? c.panel : 'transparent'),
        ...(isWeb ? { cursor: 'pointer', transition: `background-color 180ms ${EASE}, border-color 180ms ${EASE}` } : {}),
      }}
    >
      <Text
        tag="span"
        weight={isActive ? 'bold' : 'medium'}
        style={{ color: isActive ? c.accent : c.ink, fontSize: 13.5 }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function UseCaseRow({ data }) {
  const { c, isXS } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Link href={`/use-cases/${data.slug}`} asChild>
      <Pressable
        {...handlers}
        accessibilityRole="link"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 16,
          paddingVertical: isXS ? 16 : 20,
          borderTopWidth: 1,
          borderTopColor: c.rule,
          ...(isWeb ? { cursor: 'pointer' } : {}),
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <Text
            tag="h3"
            weight="semibold"
            style={{
              color: hovered ? c.accent : c.ink,
              fontSize: isXS ? 16.5 : 18,
              lineHeight: 24,
              letterSpacing: -0.2,
              ...(isWeb ? { transition: `color 180ms ${EASE}` } : {}),
            }}
          >
            {data.name}
          </Text>
          {data.subtitle ? (
            <Text tag="p" numberOfLines={2} style={{ color: c.muted, fontSize: 14.5, lineHeight: 21, marginBottom: 0 }}>
              {data.subtitle}
            </Text>
          ) : null}
        </View>
        <Icon name="arrow-right" size={13} color={hovered ? c.accent : c.faint} accessibilityElementsHidden />
      </Pressable>
    </Link>
  );
}

function UseCaseGroup({ label, items }) {
  const { c } = useLanding();

  if (!items.length) return null;

  return (
    <SemanticBlock tag="section" aria-label={label} style={{ width: '100%' }}>
      <MicroLabel style={{ marginBottom: 4 }}>{label}</MicroLabel>
      <View>
        {items.map((item) => (
          <UseCaseRow key={item.slug} data={item} />
        ))}
        <View style={{ height: 1, backgroundColor: c.rule }} />
      </View>
    </SemanticBlock>
  );
}

export default function UseCasesIndexPage() {
  const { colors, theme } = useTheme();
  const { c, isXS, width, display } = useLanding();
  const isDark = isDarkTheme(theme);
  const [activeCategory, setActiveCategory] = useState('all');
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const headline = display({ min: 36, max: 68, leading: 0.98, tracking: -0.042 });

  useLandingStyles();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await useCasesApi.list();
        if (!alive) return;
        setUseCases(Array.isArray(data?.useCases) ? data.useCases : []);
      } catch {
        if (!alive) return;
        setUseCases([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filteredUseCases = useMemo(() => {
    if (activeCategory === 'all') return useCases;
    return useCases.filter((item) => item.hubCategory === activeCategory);
  }, [activeCategory, useCases]);

  const groups = useMemo(() => {
    if (activeCategory !== 'all') {
      const match = USE_CASE_HUB_CATEGORIES.find((cat) => cat.value === activeCategory);
      return [{
        key: activeCategory,
        label: match?.label || CATEGORY_LABELS[activeCategory] || activeCategory,
        items: filteredUseCases,
      }];
    }

    const byCategory = new Map();
    for (const item of useCases) {
      const key = item.hubCategory || 'business';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(item);
    }

    const grouped = [];
    for (const key of CATEGORY_ORDER) {
      const items = byCategory.get(key);
      if (!items?.length) continue;
      grouped.push({
        key,
        label: CATEGORY_LABELS[key] || key,
        items,
      });
    }
    return grouped;
  }, [activeCategory, filteredUseCases, useCases]);

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  }), [colors]);

  return (
    <View style={containerStyle}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Gab AI Use Cases: AI Tools for Every Profession"
        description="Explore AI use cases for writers, marketers, developers, teachers, homeschoolers, sales teams, designers, and more. One platform, every creative and business tool you need."
        canonical="/use-cases"
        keywords="AI use cases, AI tools, AI for business, AI for creators, AI for developers, AI for homeschoolers, Gab AI"
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole="main"
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Section
          label="AI use cases by profession"
          top={false}
          contentStyle={{
            paddingTop: fluid(width, 104, 152),
            paddingBottom: fluid(width, 28, 40),
          }}
        >
          <View style={{ maxWidth: 760 }}>
            <Kicker>Use cases</Kicker>
            <DisplayHeading
              tag="h1"
              mask={false}
              type={headline}
              style={{ marginTop: 22 }}
              lines={['Built for how you work']}
            />
            <Lede max={520} style={{ marginTop: 18 }}>
              One AI platform, every tool you need. See how Gab AI is purpose-built for your profession.
            </Lede>
          </View>
        </Section>

        <SemanticBlock
          tag="nav"
          aria-label="Filter use cases by category"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: fluid(width, 20, 64),
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: fluid(width, 36, 52),
          }}
        >
          {USE_CASE_HUB_CATEGORIES.map((category) => (
            <FilterPill
              key={category.value}
              label={category.label}
              isActive={activeCategory === category.value}
              onPress={() => setActiveCategory(category.value)}
            />
          ))}
        </SemanticBlock>

        <SemanticBlock
          tag="section"
          aria-label="All use cases"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: fluid(width, 20, 64),
            paddingBottom: fluid(width, 48, 72),
            minHeight: 200,
            alignItems: loading ? 'center' : undefined,
            justifyContent: loading ? 'center' : undefined,
          }}
        >
          {loading ? (
            <ActivityIndicator size="large" color={c.muted} />
          ) : (
            <Reveal>
              <View style={{ gap: isXS ? 36 : 48 }}>
                {groups.map((group) => (
                  <UseCaseGroup key={group.key} label={group.label} items={group.items} />
                ))}
              </View>
            </Reveal>
          )}
        </SemanticBlock>

        <ClosingBand
          kicker="Don't see your industry?"
          lines={['Gab AI works for everyone']}
          lede="Try it free and discover what AI can do for your work. Same account, same credits, every model."
          primary={{ label: 'Try Gab AI free', href: '/signup' }}
          secondary={{ label: 'Compare plans and pricing', href: '/pricing' }}
          trust={['No credit card required', 'Cancel anytime']}
        />

        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
